/**
 * Iter 67ci — Popular Destinations strip, CMS-powered.
 *
 * Fetches `/public/blog?category=destinations&featured=true&limit=6` and
 * renders up to 6 featured destination posts. If the CMS returns nothing
 * yet (cold install) we fall through to the curated Albanian-city set so
 * the section never blanks — vendors keep their homepage promise even
 * before publishing.
 *
 * Iter 67en++++ (Feb 2026): Refocused fallback to Albanian cities (the
 * markets RentalCar.al actually serves) and deep-link each card to the
 * existing /car-rental/:slug SEO landing page so the section doubles as
 * an internal-link booster for those pages.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { MapPin, Sparkles, ArrowRight } from "lucide-react";

// Curated Albanian-city fallback. Each entry deep-links into the
// matching /car-rental/:slug landing page (slugs verified against
// locations_catalog: see routes/locations_catalog.py).
// Albania-themed images, all served from the platform's own object storage
// (AI-generated for the hero slider and reused here so the cards look
// cohesive with the rest of the site). Mapped thematically:
//   • Tirana Airport / Berat → Tirana skyline (urban silhouette)
//   • Sarandë / Durrës        → Albanian Riviera (turquoise coastal road)
//   • Gjirokastër / Shkodër   → Albanian Alps (mountain pass)
const _IMG_TIRANA   = "rentalcar/uploads/hero/albania-tirana-skyline-fb769fff.png";
const _IMG_RIVIERA  = "rentalcar/uploads/hero/albania-albanian-riviera-5db3eb8b.png";
const _IMG_ALPS     = "rentalcar/uploads/hero/albania-albanian-alps-692c35b0.png";

const FALLBACK = [
  {
    id: "fb-tirana-airport",
    country: "Albania",
    title: "Tirana Airport",
    excerpt: "Land at TIA, drive away in minutes — meet-and-greet hosts curbside.",
    featured_image: _IMG_TIRANA,
    slug: "tirana-airport-albania",
  },
  {
    id: "fb-saranda",
    country: "Albania",
    title: "Sarandë",
    excerpt: "Gateway to the Albanian Riviera — turquoise coves and Ksamil islands.",
    featured_image: _IMG_RIVIERA,
    slug: "sarande-albania",
  },
  {
    id: "fb-berat",
    country: "Albania",
    title: "Berat",
    excerpt: "UNESCO city of a thousand windows — stone houses stacked on Mount Tomorr.",
    featured_image: _IMG_TIRANA,
    slug: "berat-albania",
  },
  {
    id: "fb-gjirokaster",
    country: "Albania",
    title: "Gjirokastër",
    excerpt: "Ottoman castle town, slate-roofed houses, and the gateway to the south.",
    featured_image: _IMG_ALPS,
    slug: "gjirokaster-albania",
  },
  {
    id: "fb-durres",
    country: "Albania",
    title: "Durrës",
    excerpt: "Adriatic beaches, Roman amphitheatre, and Albania's biggest port.",
    featured_image: _IMG_RIVIERA,
    slug: "durres-albania",
  },
  {
    id: "fb-shkoder",
    country: "Albania",
    title: "Shkodër",
    excerpt: "Lake Shkodër, Rozafa castle, and the route into the Albanian Alps.",
    featured_image: _IMG_ALPS,
    slug: "shkoder-albania",
  },
];

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

export default function PopularDestinationsSection() {
  const [posts, setPosts] = useState(null); // null = loading, [] = empty
  // Iter 79 — admin-curated cards take precedence over CMS posts + fallback.
  const [custom, setCustom] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/public/destination-cards").catch(() => ({ data: [] })),
      api.get("/public/blog", { params: { category: "destinations", limit: 6 } }).catch(() => ({ data: [] })),
    ]).then(([cardsR, blogR]) => {
      if (!alive) return;
      setCustom(cardsR.data || []);
      setPosts(blogR.data || []);
    });
    return () => { alive = false; };
  }, []);

  const isCustom = !!(custom && custom.length > 0);
  const isLive = !isCustom && !!(posts && posts.length > 0);
  const items = isCustom
    ? custom.slice(0, 6).map((c) => ({ ...c, featured_image: c.image_path }))
    : (posts && posts.length > 0 ? posts : FALLBACK);

  return (
    <section className="bg-neutral-50/60 py-20" data-testid="popular-destinations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-3 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Top destinations
            </div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">Where renters love to drive</h2>
            <p className="mt-2 text-neutral-600 text-sm max-w-xl">Coast to castle: city-by-city picks curated by RentalCar.al hosts in Albania.</p>
          </div>
          <Link to="/cars?country=Albania" className="text-sm font-medium text-blue-600 hover:underline" data-testid="popular-destinations-see-all">
            Browse all cars <ArrowRight className="inline h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((d) => {
            const inner = (
              <>
                <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                  <img
                    src={_resolveSrc(d.featured_image) || FALLBACK[0].featured_image}
                    alt={d.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-bold mb-2">
                    <MapPin className="h-3 w-3" /> {d.country || "Albania"}
                  </div>
                  <h3 className="font-display text-lg font-semibold text-neutral-900">{d.title}</h3>
                  <p className="text-xs text-neutral-500 mt-1 line-clamp-2">
                    {d.excerpt || (isCustom ? "" : isLive ? "Read the full guide" : "Full guide coming soon")}
                  </p>
                </div>
              </>
            );
            // Custom admin cards use their own link (internal path or full
            // URL); CMS posts deep-link to /blog/destinations/{slug};
            // fallback entries deep-link to /car-rental/{slug}.
            const href = isCustom
              ? (d.link_url || null)
              : isLive
                ? (d.slug ? `/blog/destinations/${d.slug}` : null)
                : (d.slug ? `/car-rental/${d.slug}` : null);
            const isExternal = !!href && /^https?:\/\//i.test(href);
            const cardClass = "group rounded-3xl overflow-hidden bg-white border border-border hover:shadow-lg transition-all";
            if (href && isExternal) {
              return (
                <a
                  key={d.id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardClass}
                  data-testid={`popular-destination-${d.title || d.id}`}
                >
                  {inner}
                </a>
              );
            }
            return href ? (
              <Link
                key={d.id}
                to={href}
                className="group rounded-3xl overflow-hidden bg-white border border-border hover:shadow-lg transition-all"
                data-testid={`popular-destination-${d.title || d.id}`}
              >
                {inner}
              </Link>
            ) : (
              <article
                key={d.id}
                className="group rounded-3xl overflow-hidden bg-white border border-border hover:shadow-lg transition-all"
                data-testid={`popular-destination-${d.title || d.id}`}
              >
                {inner}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
